import { type ReactNode, useRef } from 'react';
import { Link } from 'react-router-dom';

import { REPORT_FLOW_COVER_ANCHOR_ID } from './convert-grid-report-to-flow';
import styles from './ReportFlow.module.css';
import {
  ReportFlowBlockView,
  type ReportFlowLeafContentRenderer,
} from './ReportFlowBlockView';
import {
  type ReportFlowCoverParameter,
  ReportFlowCoverParameters,
} from './ReportFlowCoverParameters';
import { ReportFlowToc } from './ReportFlowToc';
import { useReportFlowScrollSpy } from './report-flow-scroll';
import type { ReportFlowDocument } from './report-flow-types';

type ReportFlowBreadcrumb = { label: string; href: string; current: string };

export const ReportFlowHeaderContent = ({
  doc,
  breadcrumb,
  headerActions,
}: {
  doc: ReportFlowDocument;
  breadcrumb?: ReportFlowBreadcrumb;
  headerActions?: ReactNode;
}) => (
  <>
    {breadcrumb && (
      <nav aria-label="Breadcrumb">
        <ol className={styles.breadcrumb}>
          <li>
            <Link to={breadcrumb.href} className={styles.breadcrumbLink}>
              {breadcrumb.label}
            </Link>
          </li>
          <li className={styles.breadcrumbCurrent} aria-current="page">
            {breadcrumb.current}
          </li>
        </ol>
      </nav>
    )}
    <div className={styles.headerTopRow}>
      {doc.createdLabel && (
        <p className={styles.createdLabel}>{doc.createdLabel}</p>
      )}
      {headerActions && (
        <div className={styles.headerActions}>{headerActions}</div>
      )}
    </div>
    <h1 className={styles.title} data-testid="report-flow-title">
      {doc.title}
    </h1>
    {doc.description && <p className={styles.description}>{doc.description}</p>}
  </>
);

export const ReportFlowDocumentView = ({
  doc,
  parameters,
  headerActions,
  breadcrumb,
  renderLeafContent,
}: {
  doc: ReportFlowDocument;
  parameters: ReportFlowCoverParameter[];
  headerActions?: ReactNode;
  breadcrumb?: ReportFlowBreadcrumb;
  renderLeafContent?: ReportFlowLeafContentRenderer;
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { activeSectionId, updateActiveSection } = useReportFlowScrollSpy(
    doc,
    scrollContainerRef
  );

  return (
    <div className={styles.flowContainer}>
      <div className={styles.viewport} data-testid="report-flow-view">
        <ReportFlowToc
          doc={doc}
          activeSectionId={activeSectionId}
          scrollContainerRef={scrollContainerRef}
        />
        <div
          ref={scrollContainerRef}
          className={styles.scrollArea}
          onScroll={updateActiveSection}
        >
          <div className={styles.document}>
            <header className={styles.header} id={REPORT_FLOW_COVER_ANCHOR_ID}>
              <ReportFlowHeaderContent
                doc={doc}
                breadcrumb={breadcrumb}
                headerActions={headerActions}
              />
              <ReportFlowCoverParameters parameters={parameters} />
            </header>
            {doc.sections.map((section) => (
              <section
                key={section.id}
                id={section.anchorId}
                className={styles.section}
                data-testid="report-flow-section"
              >
                {section.blocks.map((block) => (
                  <ReportFlowBlockView
                    key={block.id}
                    block={block}
                    renderLeafContent={renderLeafContent}
                  />
                ))}
              </section>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
